import type { UserRole } from "@connected-repo/zod-schemas/enums.zod";
import type { Strings } from "@frontend/strings/strings.types";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

export interface NavItem {
	/** Key into `Strings["nav"]`; rendered in the login's language. */
	labelKey: keyof Strings["nav"];
	path: string;
	icon: React.ReactNode;
	/** Admin-only items. Everyone else sees only their own data. */
	adminOnly?: boolean;
}

/**
 * One list drives the desktop sidebar and the mobile bottom bar. Customers is
 * home: the studio opens from a customer (or New customer) and hides the
 * navigation while it is open.
 */
const navItems: NavItem[] = [
	{ labelKey: "customers", path: "/customers", icon: <GroupRoundedIcon /> },
	{ labelKey: "dashboard", path: "/dashboard", icon: <InsightsRoundedIcon /> },
	{ labelKey: "users", path: "/admin/users", icon: <ManageAccountsRoundedIcon />, adminOnly: true },
	{ labelKey: "settings", path: "/admin/settings", icon: <TuneRoundedIcon />, adminOnly: true },
];

export const isAdminRole = (role: UserRole | null | undefined): boolean => role === "admin";

export const navItemsForRole = (role: UserRole | null | undefined): NavItem[] =>
	navItems.filter((item) => !item.adminOnly || isAdminRole(role));

/** Index of the item whose path is the longest prefix of `pathname`, or -1. */
export const activeNavIndex = (items: NavItem[], pathname: string): number => {
	let best = -1;
	items.forEach((item, index) => {
		const matches = pathname === item.path || pathname.startsWith(`${item.path}/`);
		if (matches && (best === -1 || item.path.length > (items[best]?.path.length ?? 0))) best = index;
	});
	return best;
};
